import {
  KeyboardEvent,
  useCallback,
  useId,
  useRef,
  useState,
} from "react";
import styles from "./ChipInput.module.css";

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  modified?: boolean;
  disabled?: boolean;
  /** Validator. Return error string or null/undefined to accept. */
  validate?: (chip: string) => string | null | undefined;
  /** Reject duplicates (case-insensitive). Default true. */
  uniqueCaseInsensitive?: boolean;
  label?: string;
  /**
   * Accessible name for the text input when no visible `label` is rendered —
   * e.g. inside a card that already captions the field visually.
   */
  inputAriaLabel?: string;
  helperText?: string;
}

/**
 * ChipInput — multi-text input with Enter-to-add / × delete.
 *
 * Mockup: design-system.html L196-L214; project-config.html L506-L512,
 * L711-L717, L737-L741.
 *
 * It once painted chips added since the last save in warn-soft
 * (project-config.html L599-L605). The only caller of that was
 * `chat.profanity_words`, which now edits its list in a dialog of its own
 * (t-102), and the prop went with it rather than staying as a feature nothing
 * could reach.
 */
export function ChipInput({
  value,
  onChange,
  placeholder,
  modified,
  disabled,
  validate,
  uniqueCaseInsensitive = true,
  label,
  inputAriaLabel,
  helperText,
}: Props) {
  const reactId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isDuplicate = useCallback(
    (chip: string, list: string[]) => {
      if (!uniqueCaseInsensitive) return list.includes(chip);
      const needle = chip.toLowerCase();
      return list.some((x) => x.toLowerCase() === needle);
    },
    [uniqueCaseInsensitive],
  );

  const tryAdd = useCallback(
    (raw: string): boolean => {
      const chip = raw.trim();
      if (!chip) return false;
      if (isDuplicate(chip, value)) {
        setError("Duplicate value");
        return false;
      }
      if (validate) {
        const msg = validate(chip);
        if (msg) {
          setError(msg);
          return false;
        }
      }
      setError(null);
      onChange([...value, chip]);
      return true;
    },
    [value, onChange, validate, isDuplicate],
  );

  const removeAt = useCallback(
    (idx: number) => {
      const next = value.slice(0, idx).concat(value.slice(idx + 1));
      onChange(next);
    },
    [value, onChange],
  );

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    if (e.key === "Enter") {
      e.preventDefault();
      if (tryAdd(draft)) setDraft("");
      return;
    }
    if (e.key === "Backspace" && draft === "" && value.length > 0) {
      e.preventDefault();
      removeAt(value.length - 1);
      setError(null);
      return;
    }
    // Comma also adds; handled here so the comma never lands in the input.
    if (e.key === ",") {
      e.preventDefault();
      if (tryAdd(draft)) setDraft("");
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    // If the user paste/types comma(s), split out chips on each comma.
    if (next.includes(",")) {
      const parts = next.split(",");
      const tail = parts.pop() ?? "";
      const acc = [...value];
      let failed = false;
      for (const p of parts) {
        const t = p.trim();
        if (!t) continue;
        if (isDuplicate(t, acc)) {
          setError("Duplicate value");
          failed = true;
          continue;
        }
        if (validate) {
          const msg = validate(t);
          if (msg) {
            setError(msg);
            failed = true;
            continue;
          }
        }
        acc.push(t);
      }
      if (acc.length !== value.length) onChange(acc);
      setDraft(tail);
      if (!failed && parts.some((p) => p.trim())) setError(null);
      return;
    }
    setDraft(next);
    if (error) setError(null);
  }

  function handleBlur() {
    if (disabled) return;
    if (draft.trim()) {
      if (tryAdd(draft)) setDraft("");
    }
  }

  const fieldClass = [
    styles.field,
    modified ? styles.modified : "",
    disabled ? styles.disabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  const inputId = reactId;
  const helperId = `${reactId}-help`;

  return (
    <div className={styles.wrap}>
      {label && (
        <label className={styles.label} htmlFor={inputId}>
          {label}
        </label>
      )}
      <div
        className={fieldClass}
        onClick={(e) => {
          if (disabled) return;
          // Don't steal focus from chip × buttons.
          if ((e.target as HTMLElement).closest("button")) return;
          inputRef.current?.focus();
        }}
      >
        {value.map((chip, i) => {
          return (
            <span key={`${chip}-${i}`} className={styles.chip}>
              {chip}
              {!disabled && (
                <button
                  type="button"
                  className={styles.remove}
                  aria-label={`Remove ${chip}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAt(i);
                  }}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
        <input
          ref={inputRef}
          id={inputId}
          className={styles.input}
          type="text"
          aria-label={label ? undefined : inputAriaLabel}
          value={draft}
          placeholder={value.length === 0 ? placeholder : undefined}
          disabled={disabled}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          aria-describedby={error || helperText ? helperId : undefined}
          aria-invalid={error ? true : undefined}
        />
      </div>
      {(error || helperText) && (
        <div
          id={helperId}
          className={error ? styles.helperError : styles.helper}
        >
          {error || helperText}
        </div>
      )}
    </div>
  );
}
